import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, EventType, Router } from '@angular/router';
import { filter } from 'rxjs';

const MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]{4,}$/;

@Injectable({ providedIn: 'root' })
export class GoogleAnalyticsService {
  private initialized = false;

  constructor(private readonly router: Router) {}

  /**
   * Loads gtag.js and starts page-view tracking. No-op unless measurementId
   * is a valid GA4 id and the app is not embedded in another site
   * (index.html uses referrer=unsafe-url; GA inside a host portal would
   * enable cross-site correlation). Never throws — analytics must never
   * break the app.
   */
  init(measurementId: string, isEmbedded: boolean): void {
    try {
      if (this.initialized || isEmbedded || !this.isValidId(measurementId)) {
        return;
      }
      this.initialized = true;
      this.injectGtag(measurementId);
      this.trackPageViews();
    } catch (e) {
      console.warn('Google Analytics initialization failed:', e);
    }
  }

  private isValidId(id: string): boolean {
    return (
      !!id &&
      !id.toUpperCase().includes('PLACEHOLDER') &&
      MEASUREMENT_ID_PATTERN.test(id)
    );
  }

  private injectGtag(measurementId: string): void {
    window.dataLayer = window.dataLayer ?? [];
    window.gtag = function () {
      // GA requires the Arguments object itself on the dataLayer, not an array
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.addEventListener('error', () =>
      console.warn('Google Analytics script failed to load')
    );
    document.head.appendChild(script);
  }

  private trackPageViews(): void {
    this.router.events
      .pipe(filter((event) => event.type === EventType.NavigationEnd))
      .subscribe(() => this.sendPageView());

    // The initial navigation may already be done before init() runs
    if (this.router.navigated) {
      this.sendPageView();
    }
  }

  private sendPageView(): void {
    try {
      // Route template only (e.g. '/services/:id'): concrete URLs carry
      // citizen-case ids in path params and OAuth code/state in query params,
      // which must never reach Google. page_location and page_referrer are
      // overridden explicitly because gtag.js otherwise attaches the real
      // location.href and document.referrer (full concrete URLs under this
      // app's referrer=unsafe-url policy) to every hit.
      const template = this.buildRouteTemplate();
      const sanitizedLocation = window.location.origin + template;
      window.gtag?.('set', {
        page_location: sanitizedLocation,
        page_referrer: window.location.origin
      });
      window.gtag?.('event', 'page_view', {
        page_path: template,
        page_location: sanitizedLocation,
        page_title: document.title
      });
    } catch (e) {
      console.warn('Google Analytics page_view failed:', e);
    }
  }

  private buildRouteTemplate(): string {
    const segments: string[] = [];
    let route: ActivatedRouteSnapshot | null =
      this.router.routerState.snapshot.root;
    while (route) {
      const path = route.routeConfig?.path;
      if (path) {
        segments.push(path);
      }
      route = route.firstChild;
    }
    return `/${segments.join('/')}`;
  }
}
